/**
 * Pure helpers for Check-in New list: fetch params, doc mapping, date formatting.
 */
import CustomerConfig from "../../azure/CustomerConfig";
import {
  ONEDRIVE_ROOT,
  SEARCH_VALUE_ENCODING,
  DOCUMENT_MODE,
  ONEDRIVE_FILE_TYPES,
} from "../../redux/constants/metaData";
import { getFileName, getFileIconPath, getFileExtension } from "../../functions/helperMethod";

/**
 * @param {{ channel?: { id?: string, membershipType?: string, ownerGroupId?: string }, team?: { groupId?: string }, user?: { tenant?: { id?: string } } }} teamsContext
 * @returns {{ groupId: string, channelId: string, fileRoot: string }}
 */
export function resolveGroupId(teamsContext) {
  const channelId = teamsContext?.channel?.id ?? "";
  const groupId =
    teamsContext?.channel != null
      ? teamsContext.channel.membershipType === "Private"
        ? teamsContext.channel.ownerGroupId
        : teamsContext?.team?.groupId
      : "";
  const fileRoot =
    !groupId && !channelId ? ONEDRIVE_ROOT.mainMenu : ONEDRIVE_ROOT.channelDoc;
  return { groupId, channelId, fileRoot };
}

/**
 * @param {string} [searchValue]
 * @returns {string}
 */
export function encodeSearchValue(searchValue) {
  let text = searchValue ?? "";
  if (text && text.includes(SEARCH_VALUE_ENCODING.HASH_SYMBOL)) {
    text = text.replace(
      new RegExp(SEARCH_VALUE_ENCODING.HASH_SYMBOL, "g"),
      SEARCH_VALUE_ENCODING.HASH_ENCODED
    );
  }
  return text;
}

/** Extensions grouped by OneDrive filter type (matches Working Documents). */
const ONEDRIVE_WORD_EXTENSIONS = new Set([
  "DOC",
  "DOCX",
  "DOCM",
  "DOT",
  "DOTM",
  "DOTX",
  "RTF",
  "ODT",
]);
const ONEDRIVE_EXCEL_EXTENSIONS = new Set([
  "XLS",
  "XLSX",
  "XLSM",
  "CSV",
  "XLSB",
  "ODS",
  "XLT",
  "XLTX",
  "XLTM",
]);
const ONEDRIVE_PPT_EXTENSIONS = new Set([
  "PPT",
  "PPTX",
  "PPTM",
  "POTM",
  "POTX",
  "PPS",
  "PPSX",
  "PPSM",
  "POT",
]);
const ONEDRIVE_PDF_EXTENSIONS = new Set(["PDF"]);

function getAllowedExtensionsForOneDriveFileType(oneDriveFileType) {
  switch (oneDriveFileType) {
    case ONEDRIVE_FILE_TYPES.docx:
      return ONEDRIVE_WORD_EXTENSIONS;
    case ONEDRIVE_FILE_TYPES.xlsx:
      return ONEDRIVE_EXCEL_EXTENSIONS;
    case ONEDRIVE_FILE_TYPES.pptx:
      return ONEDRIVE_PPT_EXTENSIONS;
    case ONEDRIVE_FILE_TYPES.pdf:
      return ONEDRIVE_PDF_EXTENSIONS;
    default:
      return null;
  }
}

/**
 * When search and file-type filter are both active, get-onedrive-docs is queried by
 * search text only; file type is enforced on the client after the response.
 */
export function resolveOneDriveApiFileType(searchValue, oneDriveFileType) {
  const term = (searchValue ?? "").trim();
  const hasFileType =
    Boolean(oneDriveFileType) &&
    oneDriveFileType !== ONEDRIVE_FILE_TYPES.allFiles;

  if (term && hasFileType) {
    return "";
  }
  return oneDriveFileType ?? "";
}

/**
 * Prefer the last executed search; fall back to the current input when the user
 * typed a term but has not clicked Search yet (e.g. filter change after typing).
 */
export function resolveEffectiveSearchValue(appliedSearchValue, searchValue) {
  const applied = (appliedSearchValue ?? "").trim();
  if (applied.length > 0) return applied;
  return (searchValue ?? "").trim();
}

/**
 * @param {object} opts
 * @param {*} opts.teamsContext
 * @param {string} opts.searchValue
 * @param {string} opts.oneDriveSelectedFileType
 * @param {number} opts.pageSize
 * @param {string} [opts.folderId] - Graph drive item id when browsing inside a folder
 * @returns {{ teamId: string, channelId: string, tenantId: string, pageSize: number, searchText: string, documentType: string, fileRoot: string, isOnedrive: boolean, folderId: string }}
 */
export function buildFetchParams({
  teamsContext,
  searchValue,
  oneDriveSelectedFileType,
  pageSize,
  folderId = "",
}) {
  const { groupId, channelId, fileRoot } = resolveGroupId(teamsContext);
  return {
    teamId: groupId,
    channelId,
    tenantId: teamsContext?.user?.tenant?.id ?? "",
    pageSize: pageSize ?? 20,
    searchText: encodeSearchValue(searchValue),
    documentType: resolveOneDriveApiFileType(searchValue, oneDriveSelectedFileType),
    fileRoot,
    isOnedrive: true,
    folderId: folderId ?? "",
  };
}


export function filterDocsByOneDriveFileType(documents, oneDriveFileType) {
  if (!oneDriveFileType || oneDriveFileType === ONEDRIVE_FILE_TYPES.allFiles) {
    return documents ?? [];
  }
  const allowedExtensions = getAllowedExtensionsForOneDriveFileType(oneDriveFileType);
  if (!allowedExtensions) return documents ?? [];

  return (documents ?? []).filter((item) => {
    if (isOneDriveFolderItem(item)) return false;
    const ext = getFileExtension(item?.name);
    return allowedExtensions.has(ext?.toUpperCase());
  });
}

/** Always apply the selected file-type filter to displayed rows. */
export function filterDocsForDisplay(documents, { oneDriveFileType }) {
  return filterDocsByOneDriveFileType(documents, oneDriveFileType);
}

export function isOneDriveFileTypeFilterActive(oneDriveFileType) {
  return (
    Boolean(oneDriveFileType) &&
    oneDriveFileType !== ONEDRIVE_FILE_TYPES.allFiles
  );
}


export function getOneDriveClientFilterFetchSize() {
  const config = new CustomerConfig();
  const limit = config.getDataLimit(config.get_OneDriveDataString());
  return limit > 0 ? limit : 150;
}


export function resolveOneDriveListFetchPaging({
  oneDriveFileType,
  pageIndex,
  pageSize = 20,
}) {
  if (isOneDriveFileTypeFilterActive(oneDriveFileType)) {
    return {
      recFrom: 0,
      recSize: getOneDriveClientFilterFetchSize(),
      useClientSidePaging: true,
    };
  }
  return {
    recFrom: pageIndex * pageSize,
    recSize: pageSize,
    useClientSidePaging: false,
  };
}

export function paginateOneDriveDocs(documents, pageIndex, pageSize) {
  const start = pageIndex * pageSize;
  return (documents ?? []).slice(start, start + pageSize);
}

/**
 * @param {string|number|Date} [lastModifiedDateTime]
 * @returns {string}
 */
export function formatModifiedText(lastModifiedDateTime) {
  const lastMod = lastModifiedDateTime ? new Date(lastModifiedDateTime) : null;
  if (!lastMod || Number.isNaN(lastMod.getTime())) {
    return "";
  }
  const year = lastMod.getFullYear();
  const month = String(lastMod.getMonth() + 1).padStart(2, "0");
  const day = String(lastMod.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * @param {Array<{ id?: string, name?: string, file?: { mimeType?: string }, fileState?: string, status?: string, lastModifiedDateTime?: string, lastModifiedBy?: { user?: { displayName?: string } }, microsoftGraphDownloadUrl?: string, webUrl?: string }>} documents
 * @returns {Array<{ key: string, iconName: string, name: string, title: string, modified: string, modifiedby: string, downloadlink?: string, fileType: string, weblink?: string, webLinkUrl?: string, documentMode: string }>}
 */
export function isOneDriveFolderItem(item) {
  return item?.folder != null && item.folder !== undefined;
}

export function mapDocsToItems(documents) {
  if (!documents?.length) return [];
  return documents.map((item) => {
    const isFolder = isOneDriveFolderItem(item);
    const fileExtension =
      !isFolder && item?.name && item.name.includes(".")
        ? item.name.split(".").pop()
        : "";

    const iconPath = isFolder
      ? null
      : getFileIconPath(item.file?.mimeType, {
          FileExtension: fileExtension,
          FileType: item.file?.mimeType,
          // Let the icon vary for released/checked-out variants when backend provides it.
          FileState: item.fileState ?? "",
          Status: item.status ?? "",
        });
    const webUrl = item.webUrl;
    return {
      key: item.id,
      id: item.id,
      iconName: iconPath,
      isFolder,
      name: item.name,
      title: isFolder ? item.name : getFileName(item.name),
      modified: formatModifiedText(item.lastModifiedDateTime),
      modifiedby: item.lastModifiedBy?.user?.displayName ?? "",
      downloadlink: item.microsoftGraphDownloadUrl,
      fileType: item.file?.mimeType ?? "",
      weblink: webUrl,
      webLinkUrl: webUrl,
      documentMode: DOCUMENT_MODE.oneDrive,
      docNo: item.DocNo ?? "",
      docRev: item.DocRev ?? "",
      docClass: item.DocClass ?? "",
      docSheet: item.DocSheet ?? "",
      fileState: item.fileState ?? "",
      metaData: item.MetaData ?? "",
      driveId: item.DriveId ?? "",
      itemsId: item.ItemsId ?? "",
    };
  });
}
